import React, { useState, useMemo, useCallback } from "react";

import host from '../../assets/js/Host';
const API_BASE = host + "dashbord";

function DonutChart({ delivered, rejected, size = 140 }) {
  const clean = delivered - rejected;
  const total = delivered;
  if (total === 0) return null;
  const r = (size - 20) / 2, cx = size / 2, cy = size / 2, c = 2 * Math.PI * r;
  const cleanPct = clean / total, rejPct = rejected / total;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F5F9" strokeWidth={16} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#10B981" strokeWidth={16}
        strokeDasharray={`${cleanPct * c} ${c - cleanPct * c}`} strokeDashoffset={0} strokeLinecap="round"
        style={{ transition: "all .6s ease" }} />
      {rejected > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EF4444" strokeWidth={16}
        strokeDasharray={`${rejPct * c} ${c - rejPct * c}`} strokeDashoffset={-(cleanPct * c)} strokeLinecap="round"
        style={{ transition: "all .6s ease" }} />}
    </svg>
  );
}

function DailyChart({ data, empId }) {
  const ed = data.filter(d => Number(d.adminId) === empId).sort((a, b) => a.date.localeCompare(b.date));
  if (ed.length === 0) return null;
  const max = Math.max(...ed.map(d => Number(d.totalDelivered)));
  const bw = Math.min(28, Math.floor(180 / ed.length));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 50, direction: "ltr" }}>
      {ed.map((d, i) => {
        const del = Number(d.totalDelivered), rej = Number(d.totalRejected || 0);
        const h = max > 0 ? (del / max) * 42 : 0;
        const rejH = del > 0 ? (rej / del) * h : 0;
        return (
          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <span style={{ fontSize: 9, fontFamily: "'IBM Plex Mono'", fontWeight: 700, color: "#64748B" }}>{del}</span>
            <div style={{ width: bw, height: h, borderRadius: "3px 3px 0 0", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              {rej > 0 && <div style={{ height: rejH, background: "#EF4444", transition: "height .5s ease" }} />}
              <div style={{ flex: 1, background: "#10B981", transition: "all .5s ease" }} />
            </div>
            <span style={{ fontSize: 8, color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>{String(d.date).slice(8, 10)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function SanaReport() {
  const today = new Date().toISOString().split("T")[0];
  const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);
  const [reportData, setReportData] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDevices, setEmployeeDevices] = useState([]);
  const [deviceFilter, setDeviceFilter] = useState("all");
  const [deviceSearch, setDeviceSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setSelectedEmployee(null);
    setEmployeeDevices([]);
    try {
      const res = await fetch(`${API_BASE}/sana-report?startDate=${startDate}&endDate=${endDate}`);
      const data = await res.json();
      setReportData(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [startDate, endDate]);

  const fetchEmployeeDevices = useCallback(async (adminId, filter) => {
    setDevicesLoading(true);
    try {
      let url = `${API_BASE}/sana-report/employee/${adminId}?startDate=${startDate}&endDate=${endDate}`;
      if (filter && filter !== "all") url += `&status=${filter}`;
      const res = await fetch(url);
      const data = await res.json();
      setEmployeeDevices(data.devices || []);
    } catch (err) { console.error(err); }
    setDevicesLoading(false);
  }, [startDate, endDate]);

  const handleSelectEmployee = (empId) => {
    if (selectedEmployee === empId) { setSelectedEmployee(null); setEmployeeDevices([]); return; }
    setSelectedEmployee(empId);
    setDeviceFilter("all");
    setDeviceSearch("");
    fetchEmployeeDevices(empId, "all");
  };

  const handleFilterChange = (f) => {
    setDeviceFilter(f);
    if (selectedEmployee) fetchEmployeeDevices(selectedEmployee, f);
  };

  const employees = reportData?.employees || [];
  const dailyBreakdown = reportData?.dailyBreakdown || [];
  const summary = reportData?.summary || {};

  const maxDel = useMemo(() => Math.max(...employees.map(e => Number(e.totalDelivered || 0)), 1), [employees]);
  const activeEmployee = useMemo(() => employees.find(e => Number(e.adminId) === selectedEmployee), [selectedEmployee, employees]);
  const dayCount = useMemo(() => Math.max(1, Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1), [startDate, endDate]);

  const filteredDevices = useMemo(() => {
    if (!deviceSearch.trim()) return employeeDevices;
    const q = deviceSearch.trim().toLowerCase();
    return employeeDevices.filter(d =>
      String(d.id).includes(q) ||
      (d.itemInName || "").toLowerCase().includes(q) ||
      (d.itemOutName || "").toLowerCase().includes(q)
    );
  }, [employeeDevices, deviceSearch]);

  return (
    <div dir="rtl" style={{ minHeight: "100vh", background: "#F1F5F9", fontFamily: "'Noto Kufi Arabic','Segoe UI',sans-serif", color: "#1E293B" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@300;400;500;600;700;800&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        .wrap{max-width:1280px;margin:0 auto;padding:20px 16px}
        .hdr{display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:12px;margin-bottom:20px;padding:20px 24px;background:#fff;border-radius:16px;border:1px solid #E2E8F0;box-shadow:0 1px 3px rgba(0,0,0,.04)}
        .hdr-t{font-size:24px;font-weight:800;color:#0F172A}.hdr-s{font-size:12px;color:#94A3B8;margin-top:2px}
        .ctrl{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
        .dtw{display:flex;align-items:center;gap:6px;background:#F8FAFC;padding:4px 8px;border-radius:10px;border:1.5px solid #E2E8F0}
        .dtl{font-size:11px;color:#94A3B8;font-weight:600}
        .dti{background:transparent;border:none;color:#334155;padding:6px 8px;font-size:13px;font-family:'IBM Plex Mono',monospace;outline:none;direction:ltr;width:130px}
        .sbtn{padding:8px 24px;background:#3B82F6;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:'Noto Kufi Arabic',sans-serif;transition:all .2s;display:flex;align-items:center;gap:6px}.sbtn:hover{background:#2563EB}.sbtn:active{transform:scale(.97)}.sbtn:disabled{background:#94A3B8;cursor:not-allowed}
        .stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:8px;margin-bottom:16px}
        .st{background:#fff;border:1px solid #E2E8F0;border-radius:12px;padding:16px;transition:transform .2s,box-shadow .2s}.st:hover{transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,.04)}
        .st-l{font-size:10px;color:#94A3B8;font-weight:700;margin-bottom:6px;letter-spacing:.4px}
        .st-v{font-size:32px;font-weight:800;font-family:'IBM Plex Mono',monospace;direction:ltr;text-align:right;line-height:1}
        .st-s{font-size:10px;color:#94A3B8;margin-top:4px}
        .mgrid{display:grid;grid-template-columns:1fr 280px;gap:12px}
        @media(max-width:860px){.mgrid{grid-template-columns:1fr}.hdr{flex-direction:column}}
        .card{background:#fff;border:1px solid #E2E8F0;border-radius:14px;padding:16px;box-shadow:0 1px 3px rgba(0,0,0,.03)}
        .card-t{font-size:13px;font-weight:700;color:#334155;margin-bottom:12px;display:flex;align-items:center;gap:8px}.card-t::before{content:'';width:3px;height:14px;background:#3B82F6;border-radius:2px}
        .emp{background:#FAFBFC;border:1.5px solid #E2E8F0;border-radius:10px;margin-bottom:5px;cursor:pointer;transition:all .15s;overflow:hidden}.emp:hover{border-color:#CBD5E1;background:#F1F5F9}.emp.sel{border-color:#3B82F6;background:#EFF6FF;box-shadow:0 0 0 3px #3B82F620}
        .emp-h{display:flex;align-items:center;padding:10px 12px;gap:10px}
        .emp-r{width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;font-family:'IBM Plex Mono',monospace;flex-shrink:0}
        .emp-n{flex:1;font-size:13px;font-weight:600;color:#1E293B}
        .emp-nums{display:flex;gap:12px;align-items:center}
        .emp-num{text-align:center}
        .emp-num-v{font-family:'IBM Plex Mono',monospace;font-size:20px;font-weight:800;line-height:1}
        .emp-num-l{font-size:9px;color:#94A3B8;margin-top:2px}
        .emp-bar{height:4px;background:#E2E8F0;margin:0 12px 8px;border-radius:2px;overflow:hidden;display:flex}
        .side{display:flex;flex-direction:column;gap:12px}
        .dbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px}
        .dsrch{flex:1;min-width:160px;background:#F8FAFC;border:1.5px solid #E2E8F0;padding:7px 12px;border-radius:8px;font-size:12px;font-family:'Noto Kufi Arabic',sans-serif;outline:none;color:#334155}.dsrch:focus{border-color:#3B82F6}.dsrch::placeholder{color:#CBD5E1}
        .dflts{display:flex;gap:4px}
        .fb{padding:5px 12px;border-radius:6px;font-size:11px;font-weight:600;border:1.5px solid #E2E8F0;background:#fff;color:#64748B;cursor:pointer;font-family:'Noto Kufi Arabic',sans-serif;transition:all .15s}.fb:hover{border-color:#CBD5E1}.fb.on{background:#0F172A;color:#fff;border-color:#0F172A}
        .dtbl{width:100%;border-collapse:collapse;table-layout:fixed}
        .dtbl th,.dtbl td{text-align:right;padding:10px 12px;border-bottom:1px solid #F1F5F9}
        .dtbl th{font-size:11px;font-weight:700;color:#94A3B8;border-bottom:2px solid #E2E8F0;position:sticky;top:0;background:#fff;z-index:1}
        .dtbl td{font-size:12px;color:#334155;vertical-align:middle}
        .dtbl tr:hover td{background:#FAFBFC}
        .dtbl .col-id{width:12%}
        .dtbl .col-in{width:30%}
        .dtbl .col-out{width:38%}
        .dtbl .col-st{width:20%}
        .mono{font-family:'IBM Plex Mono',monospace;font-weight:600;direction:ltr}
        .dscr{max-height:420px;overflow-y:auto;border:1.5px solid #E2E8F0;border-radius:10px}
        .dscr::-webkit-scrollbar{width:4px}.dscr::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:3px}
        .badge{display:inline-flex;align-items:center;padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;white-space:nowrap}
        .badge-ok{color:#059669;background:#ECFDF5;border:1px solid #05966920}
        .badge-rej{color:#EF4444;background:#FEF2F2;border:1px solid #EF444420}
        .empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:40px 20px;color:#CBD5E1}.empty-i{font-size:36px;margin-bottom:10px;opacity:.5}.empty-t{font-size:13px;font-weight:500;color:#94A3B8}
        .rcnt{font-size:11px;color:#94A3B8;font-family:'IBM Plex Mono',monospace;padding:4px 0}
        .lbar{height:3px;background:#E2E8F0;border-radius:2px;overflow:hidden;margin-bottom:12px}.lbar-i{height:100%;width:40%;background:linear-gradient(90deg,#3B82F6,#93C5FD);border-radius:2px;animation:ls 1s ease-in-out infinite}@keyframes ls{0%{transform:translateX(250%)}100%{transform:translateX(-100%)}}
      `}</style>

      <div className="wrap">
        {/* Header with search button */}
        <div className="hdr">
          <div>
            <div className="hdr-t">تقرير الصيانة</div>
            <div className="hdr-s">كم جهاز سلّم كل موظف للمخزن وكم منها راجع</div>
          </div>
          <div className="ctrl">
            <div className="dtw"><span className="dtl">من</span><input type="date" className="dti" value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
            <div className="dtw"><span className="dtl">إلى</span><input type="date" className="dti" value={endDate} onChange={e => setEndDate(e.target.value)} /></div>
            <button className="sbtn" onClick={fetchReport} disabled={loading}>
              {loading ? "جاري البحث..." : "بحث"}
            </button>
          </div>
        </div>

        {loading && <div className="lbar"><div className="lbar-i" /></div>}

        {/* Show content only after search */}
        {reportData && (
          <>
            {/* Stats */}
            <div className="stats">
              <div className="st"><div className="st-l">تم تسليمه للمخزن</div><div className="st-v" style={{ color: "#10B981" }}>{summary.totalDelivered || 0}</div><div className="st-s">{dayCount} يوم</div></div>
              <div className="st"><div className="st-l">راجع</div><div className="st-v" style={{ color: "#EF4444" }}>{summary.totalRejected || 0}</div></div>
              <div className="st"><div className="st-l">جديد</div><div className="st-v" style={{ color: "#3B82F6" }}>{summary.totalClean || 0}</div></div>
              <div className="st"><div className="st-l">نسبة الراجع</div><div className="st-v" style={{ color: (summary.rejectedRate || 0) > 15 ? "#EF4444" : "#64748B" }}>{summary.rejectedRate || 0}%</div></div>
              <div className="st"><div className="st-l">عدد الموظفين</div><div className="st-v" style={{ color: "#7C3AED" }}>{summary.totalEmployees || 0}</div></div>
            </div>

            {/* Main Grid */}
            <div className="mgrid">
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Employees */}
                <div className="card">
                  <div className="card-t">الموظفين</div>
                  {employees.length === 0 && !loading && <div className="empty"><div className="empty-i">📊</div><div className="empty-t">لا توجد بيانات لهذه الفترة</div></div>}
                  {employees.map((emp, idx) => {
                    const del = Number(emp.totalDelivered || 0);
                    const rej = Number(emp.totalRejected || 0);
                    const clean = del - rej;
                    const rejPct = del > 0 ? (rej / del) * 100 : 0;
                    const cleanPct = del > 0 ? (clean / del) * 100 : 0;
                    const barPct = (del / maxDel) * 100;
                    const isSel = selectedEmployee === Number(emp.adminId);
                    const colors = ["#3B82F6","#7C3AED","#059669","#D97706","#0284C7","#9333EA","#EA580C"];
                    const bgs = ["#EFF6FF","#F5F3FF","#ECFDF5","#FFFBEB","#F0F9FF","#FAF5FF","#FFF7ED"];
                    return (
                      <div key={emp.adminId} className={`emp ${isSel ? "sel" : ""}`} onClick={() => handleSelectEmployee(Number(emp.adminId))}>
                        <div className="emp-h">
                          <div className="emp-r" style={{ background: bgs[idx % bgs.length], color: colors[idx % colors.length] }}>{idx + 1}</div>
                          <div className="emp-n">{emp.adminName}</div>
                          <DailyChart data={dailyBreakdown} empId={Number(emp.adminId)} />
                          <div className="emp-nums">
                            <div className="emp-num"><div className="emp-num-v" style={{ color: "#10B981" }}>{del}</div><div className="emp-num-l">سلّم</div></div>
                            <div className="emp-num"><div className="emp-num-v" style={{ color: rej > 0 ? "#EF4444" : "#CBD5E1" }}>{rej}</div><div className="emp-num-l">راجع</div></div>
                          </div>
                        </div>
                        <div className="emp-bar" style={{ width: `calc(${barPct}% - 24px)`, marginRight: 12 }}>
                          <div style={{ width: `${cleanPct}%`, height: "100%", background: "#10B981", borderRadius: "2px 0 0 2px", transition: "width .7s ease" }} />
                          {rej > 0 && <div style={{ width: `${rejPct}%`, height: "100%", background: "#EF4444", borderRadius: "0 2px 2px 0", transition: "width .7s ease" }} />}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Devices Table */}
                <div className="card">
                  <div className="card-t">{activeEmployee ? `أجهزة ${activeEmployee.adminName}` : "تفاصيل الأجهزة"}</div>
                  {devicesLoading && <div className="lbar"><div className="lbar-i" /></div>}
                  {activeEmployee ? (
                    <>
                      <div className="dbar">
                        <input type="text" className="dsrch" placeholder="ابحث برقم الجهاز أو اسمه..." value={deviceSearch} onChange={e => setDeviceSearch(e.target.value)} />
                        <div className="dflts">
                          <button className={`fb ${deviceFilter === "all" ? "on" : ""}`} onClick={() => handleFilterChange("all")}>الكل ({activeEmployee.totalDelivered})</button>
                          <button className={`fb ${deviceFilter === "clean" ? "on" : ""}`} onClick={() => handleFilterChange("clean")}>جديد ({Number(activeEmployee.totalDelivered) - Number(activeEmployee.totalRejected)})</button>
                          <button className={`fb ${deviceFilter === "rejected" ? "on" : ""}`} onClick={() => handleFilterChange("rejected")} style={deviceFilter === "rejected" ? {} : { borderColor: "#FCA5A5", color: "#EF4444" }}>راجع ({activeEmployee.totalRejected})</button>
                        </div>
                      </div>
                      <div className="rcnt">{filteredDevices.length} جهاز</div>
                      <div className="dscr">
                        <table className="dtbl">
                          <thead>
                            <tr>
                              <th className="col-id">رقم الجهاز</th>
                              <th className="col-in">الجهاز الأولي</th>
                              <th className="col-out">الجهاز النهائي</th>
                              <th className="col-st">الحالة</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredDevices.map((dev) => (
                              <tr key={dev.id}>
                                <td className="col-id mono" style={{ color: "#3B82F6" }}>{dev.id}</td>
                                <td className="col-in">{dev.itemInName}</td>
                                <td className="col-out">{dev.itemOutName}</td>
                                <td className="col-st">
                                  {dev.is_rejected
                                    ? <span className="badge badge-rej">راجع</span>
                                    : <span className="badge badge-ok">جهاز جديد</span>
                                  }
                                </td>
                              </tr>
                            ))}
                            {filteredDevices.length === 0 && !devicesLoading && (
                              <tr><td colSpan={4} style={{ textAlign: "center", padding: 24, color: "#CBD5E1" }}>لا توجد نتائج</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="empty"><div className="empty-i">📱</div><div className="empty-t">اختر موظف لعرض أجهزته</div></div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="side">
                <div className="card">
                  <div className="card-t">نسبة الجديد / الراجع</div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                    <div style={{ position: "relative" }}>
                      <DonutChart delivered={summary.totalDelivered || 0} rejected={summary.totalRejected || 0} />
                      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center" }}>
                        <div style={{ fontFamily: "'IBM Plex Mono'", fontSize: 28, fontWeight: 800, color: "#0F172A" }}>{summary.totalDelivered || 0}</div>
                        <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 600 }}>جهاز</div>
                      </div>
                    </div>
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 2, background: "#10B981" }} />
                        <span style={{ flex: 1, fontSize: 12, color: "#64748B" }}>جديد</span>
                        <span style={{ fontFamily: "'IBM Plex Mono'", fontWeight: 700, fontSize: 13, color: "#10B981" }}>{summary.totalClean || 0}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 2, background: "#EF4444" }} />
                        <span style={{ flex: 1, fontSize: 12, color: "#64748B" }}>راجع</span>
                        <span style={{ fontFamily: "'IBM Plex Mono'", fontWeight: 700, fontSize: 13, color: "#EF4444" }}>{summary.totalRejected || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {activeEmployee && (() => {
                  const del = Number(activeEmployee.totalDelivered || 0);
                  const rej = Number(activeEmployee.totalRejected || 0);
                  const clean = del - rej;
                  const rate = del > 0 ? Math.round((rej / del) * 1000) / 10 : 0;
                  return (
                    <div className="card">
                      <div className="card-t">{activeEmployee.adminName}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 12, color: "#64748B" }}>سلّم للمخزن</span>
                          <span style={{ fontFamily: "'IBM Plex Mono'", fontWeight: 800, fontSize: 18, color: "#10B981" }}>{del}</span>
                        </div>
                        <div style={{ height: 6, background: "#F1F5F9", borderRadius: 4, overflow: "hidden", display: "flex" }}>
                          {clean > 0 && <div style={{ width: `${(clean / del) * 100}%`, height: "100%", background: "#10B981", transition: "width .5s ease" }} />}
                          {rej > 0 && <div style={{ width: `${(rej / del) * 100}%`, height: "100%", background: "#EF4444", transition: "width .5s ease" }} />}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <div><div style={{ fontSize: 10, color: "#94A3B8" }}>جديد</div><div style={{ fontFamily: "'IBM Plex Mono'", fontWeight: 700, fontSize: 14, color: "#10B981" }}>{clean}</div></div>
                          <div style={{ textAlign: "center" }}><div style={{ fontSize: 10, color: "#94A3B8" }}>راجع</div><div style={{ fontFamily: "'IBM Plex Mono'", fontWeight: 700, fontSize: 14, color: "#EF4444" }}>{rej}</div></div>
                          <div style={{ textAlign: "left" }}><div style={{ fontSize: 10, color: "#94A3B8" }}>نسبة الراجع</div><div style={{ fontFamily: "'IBM Plex Mono'", fontWeight: 700, fontSize: 14, color: rate > 15 ? "#EF4444" : "#64748B" }}>{rate}%</div></div>
                        </div>
                        <div style={{ paddingTop: 8, borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 11, color: "#64748B" }}>المعدل اليومي</span>
                          <span style={{ fontFamily: "'IBM Plex Mono'", fontWeight: 800, fontSize: 14, color: "#3B82F6" }}>{Math.round(del / dayCount)} جهاز/يوم</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </>
        )}

        {/* Before first search */}
        {!reportData && !loading && (
          <div className="card" style={{ textAlign: "center", padding: 60 }}>
            <div style={{ fontSize: 40, opacity: .4, marginBottom: 12 }}>📅</div>
            <div style={{ fontSize: 14, color: "#94A3B8", fontWeight: 500 }}>حدد الفترة واضغط "بحث"</div>
          </div>
        )}
      </div>
    </div>
  );
}